import {
  Environment,
  EnvironmentType,
  FeatureDefinition,
  FeatureFlag,
  FeatureFlagConfig,
  FeatureFlagKey,
} from "@/types/feature-flags";

// Feature definitions - single source of truth for what features exist
export const FEATURE_DEFINITIONS: Record<FeatureFlagKey, FeatureDefinition> = {
  [FeatureFlagKey.FOUR_POINT_SCORING]: {
    key: FeatureFlagKey.FOUR_POINT_SCORING,
    name: "Four Point Scoring",
    description: "Enable 4-point scoring system instead of 5-point",
    enabledFor: {
      environments: [Environment.RRA_QA, Environment.RRA_PRODUCTION],
    },
  },
  [FeatureFlagKey.PUBLIC_SHAREABLE_PROFILE]: {
    key: FeatureFlagKey.PUBLIC_SHAREABLE_PROFILE,
    name: "Public Shareable Profile",
    description: "Enable public shareable profile functionality",
    enabledFor: {
      environments: [
        Environment.LOCAL,
        Environment.DEV,
        Environment.PRODUCTION,
      ],
    },
  },
  [FeatureFlagKey.PPT_EXPORT_RRA]: {
    key: FeatureFlagKey.PPT_EXPORT_RRA,
    name: "PPT Export for RRA",
    description: "Enable PPT export functionality for RRA accounts",
    enabledFor: {
      environments: [Environment.RRA_QA, Environment.RRA_PRODUCTION],
    },
  },
  [FeatureFlagKey.LEADERSHIP_SPAN]: {
    key: FeatureFlagKey.LEADERSHIP_SPAN,
    name: "Leadership Span",
    description: "Enable Leadership Span™ widget for RRA accounts",
    enabledFor: {
      environments: [Environment.RRA_QA, Environment.RRA_PRODUCTION],
    },
  },
  [FeatureFlagKey.PROJECT_KANBAN_LABELS]: {
    key: FeatureFlagKey.PROJECT_KANBAN_LABELS,
    name: "Project Kanban Labels",
    description: "Enable custom labels for project kanban boards",
    enabledFor: {
      environments: [
        Environment.LOCAL,
        Environment.DEV,
        Environment.PRODUCTION,
      ],
    },
  },
};

// Account-specific overrides (can be loaded from API or config)
export const ACCOUNT_OVERRIDES: Record<
  number,
  Partial<Record<FeatureFlagKey, boolean>>
> = {
  // Example: 123: { [FeatureFlagKey.FOUR_POINT_SCORING]: true }
  // This would enable four-point-scoring for account 123 even if it's disabled by default
};

/**
 * Generate feature flag configuration for a specific environment
 */
function generateEnvironmentConfig(
  environment: EnvironmentType
): FeatureFlagConfig {
  const flags: Partial<Record<FeatureFlagKey, FeatureFlag>> = {};

  Object.values(FEATURE_DEFINITIONS).forEach((definition) => {
    const isEnabledForEnvironment =
      definition.enabledFor.environments?.includes(environment) ?? false;

    flags[definition.key] = {
      key: definition.key,
      enabled: isEnabledForEnvironment,
      description: definition.description,
      environments: definition.enabledFor.environments,
      metadata: definition.metadata,
    };
  });

  return {
    environment,
    flags,
  };
}

// Generate configurations for all environments
export const featureFlagConfigs: Record<EnvironmentType, FeatureFlagConfig> = {
  [Environment.LOCAL]: generateEnvironmentConfig(Environment.LOCAL),
  [Environment.DEV]: generateEnvironmentConfig(Environment.DEV),
  [Environment.PRODUCTION]: generateEnvironmentConfig(Environment.PRODUCTION),
  [Environment.RRA_QA]: generateEnvironmentConfig(Environment.RRA_QA),
  [Environment.RRA_PRODUCTION]: generateEnvironmentConfig(
    Environment.RRA_PRODUCTION
  ),
};

// Get current environment from Vite environment variables
export const getCurrentEnvironment = (): EnvironmentType => {
  const env = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;

  switch (env) {
    case "local":
    case "development":
      return Environment.LOCAL;
    case "dev":
      return Environment.DEV;
    case "production":
      return Environment.PRODUCTION;
    case "rraqa":
      return Environment.RRA_QA;
    case "rra-production":
      return Environment.RRA_PRODUCTION;
    default:
      return Environment.LOCAL; // Default to local for development
  }
};

// Get feature flag configuration for current environment
export const getCurrentFeatureFlagConfig = (): FeatureFlagConfig => {
  const environment = getCurrentEnvironment();
  return featureFlagConfigs[environment];
};

/**
 * Get feature flag configuration with account-specific overrides
 */
export const getFeatureFlagConfigWithAccountOverrides = (
  environment: EnvironmentType,
  accountId?: number
): FeatureFlagConfig => {
  const baseConfig = featureFlagConfigs[environment];

  if (!accountId) {
    return baseConfig;
  }

  const flags: Partial<Record<FeatureFlagKey, FeatureFlag>> = {};

  // Generate flags with account-specific logic
  Object.values(FEATURE_DEFINITIONS).forEach((definition) => {
    const isEnabledForEnvironment =
      definition.enabledFor.environments?.includes(environment) ?? false;
    const isEnabledForAccount =
      definition.enabledFor.accounts?.includes(accountId) ?? false;
    const hasAccountOverride = ACCOUNT_OVERRIDES[accountId]?.[definition.key];

    // Feature is enabled if:
    // 1. It's enabled for the environment AND (enabled for account OR no account restrictions)
    // 2. OR there's an explicit account override
    const isEnabled =
      (isEnabledForEnvironment &&
        (isEnabledForAccount || !definition.enabledFor.accounts)) ||
      hasAccountOverride === true;

    flags[definition.key] = {
      key: definition.key,
      enabled: isEnabled,
      description: definition.description,
      environments: definition.enabledFor.environments,
      metadata: definition.metadata,
    };
  });

  return {
    environment,
    flags,
  };
};

/**
 * Add account-specific override
 */
export const addAccountOverride = (
  accountId: number,
  flagKey: FeatureFlagKey,
  enabled: boolean
): void => {
  if (!ACCOUNT_OVERRIDES[accountId]) {
    ACCOUNT_OVERRIDES[accountId] = {};
  }
  ACCOUNT_OVERRIDES[accountId][flagKey] = enabled;
};

/**
 * Remove account-specific override
 */
export const removeAccountOverride = (
  accountId: number,
  flagKey: FeatureFlagKey
): void => {
  if (ACCOUNT_OVERRIDES[accountId]) {
    delete ACCOUNT_OVERRIDES[accountId][flagKey];
    if (Object.keys(ACCOUNT_OVERRIDES[accountId]).length === 0) {
      delete ACCOUNT_OVERRIDES[accountId];
    }
  }
};

/**
 * Get all feature definitions
 */
export const getFeatureDefinitions = (): Record<
  FeatureFlagKey,
  FeatureDefinition
> => {
  return FEATURE_DEFINITIONS;
};

/**
 * Check if a feature is enabled for a specific environment and account
 */
export const isFeatureEnabledFor = (
  flagKey: FeatureFlagKey,
  environment: EnvironmentType,
  accountId?: number
): boolean => {
  const definition = FEATURE_DEFINITIONS[flagKey];
  if (!definition) return false;

  const isEnabledForEnvironment =
    definition.enabledFor.environments?.includes(environment) ?? false;
  const isEnabledForAccount = accountId
    ? definition.enabledFor.accounts?.includes(accountId) ?? false
    : false;
  const hasAccountOverride = accountId
    ? ACCOUNT_OVERRIDES[accountId]?.[flagKey]
    : undefined;

  // Feature is enabled if:
  // 1. It's enabled for the environment AND (enabled for account OR no account restrictions)
  // 2. OR there's an explicit account override
  return (
    (isEnabledForEnvironment &&
      (isEnabledForAccount || !definition.enabledFor.accounts)) ||
    hasAccountOverride === true
  );
};

/**
 * Get all accounts that have a specific feature enabled
 */
export const getAccountsWithFeature = (flagKey: FeatureFlagKey): number[] => {
  const definition = FEATURE_DEFINITIONS[flagKey];
  if (!definition) return [];

  const accountsFromDefinition = definition.enabledFor.accounts || [];
  const accountsFromOverrides = Object.keys(ACCOUNT_OVERRIDES)
    .map(Number)
    .filter((accountId) => ACCOUNT_OVERRIDES[accountId][flagKey] === true);

  return [...new Set([...accountsFromDefinition, ...accountsFromOverrides])];
};
